import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useFlightSchoolCourse,
  type FlightSchoolModuleStatus,
} from "@/features/flight-school";
import { useCurrentCourse } from "@/hooks/useSelectedCourse";
import { PathConnector } from "./ChapterCard";
import StatusChapterCard, {
  type ChapterCardStatus,
} from "./StatusChapterCard";
import CourseCompletedPopup from "./CourseCompletedPopup";
import ExploreOtherCoursesCard from "./ExploreOtherCoursesCard";
import {
  clearCompletedChapterSignal,
  peekCompletedChapterSignal,
} from "./completedChapterSignal";

/**
 * Flight School path for the Home/Learn tab.
 *
 * When Flight School is the current course there's no backend course record to
 * fetch chapters from (its virtual id -1 returns 0 chapters), so the Home tab
 * builds the FS chapter path directly from the module list instead — mirroring
 * iOS (client-native/src/features/home/hooks/useHomeScreenData.ts:254-265), which
 * filters status !== "coming_soon" and renders the rest through the SAME
 * image-backed card the regular course path uses (StatusChapterCard).
 */

// iOS switches Flight School graduates to the TikTok course
// (client-native HomeScreen.tsx:399-403, PROD_TIKTOK_COURSE_ID).
const PROD_TIKTOK_COURSE_ID = 5;

// Collapse the FS module statuses onto the three card visual states.
function cardStatus(status: FlightSchoolModuleStatus): ChapterCardStatus {
  if (status === "completed") return "completed";
  if (status === "current") return "current";
  return "locked";
}

const FlightSchoolHomePath: React.FC = () => {
  const navigate = useNavigate();
  // Pull the merged module list + statuses from the hook so a backend-published
  // Flight School config flows through here too (not just the hardcoded list).
  const { modules, statuses } = useFlightSchoolCourse();
  const { setCurrentCourseID } = useCurrentCourse();

  // The card list mirrors the regular ChapterPathList, including its bottom
  // "Explore other courses" card (locked until the course is done, then
  // pressable → Courses tab). Flight School lacked this entirely.
  const visibleModules = modules.filter(
    (m) => (statuses[m.id] ?? "locked") !== "coming_soon"
  );
  const allCompleted =
    visibleModules.length > 0 &&
    visibleModules.every((m) => (statuses[m.id] ?? "locked") === "completed");

  // Graduation hand-off (iOS HomeScreen.tsx:395-413): consume the Flight
  // School completion signal emitted by useFlightSchoolFlow; when the whole
  // course is done, show the FS CourseCompletedPopup whose CTA switches the
  // current course to the TikTok course. ChapterPathList intentionally ignores
  // FS signals, so this path owns them.
  const [showGraduationPopup, setShowGraduationPopup] = useState(false);
  useEffect(() => {
    const signal = peekCompletedChapterSignal();
    if (!signal || !signal.isFlightSchool) return;
    clearCompletedChapterSignal();
    if (signal.courseCompleted) setShowGraduationPopup(true);
  }, []);

  return (
    // No standalone title: the course name already lives in the CURRENT COURSE
    // hero above (iOS doesn't repeat it here either).
    <div className="flex flex-col w-full justify-start gap-4 no-scrollbar">
      <div className="flex flex-col w-full pb-24">
        {/* iOS hides coming_soon modules from the home path entirely
            (useHomeScreenData.ts:254-265 filters status !== "coming_soon"). */}
        {visibleModules.map((m) => {
          const status = cardStatus(statuses[m.id] ?? "locked");
          const isLocked = status === "locked";

          return (
            <div key={m.id}>
              <PathConnector />
              <StatusChapterCard
                status={status}
                title={m.title}
                subtitle={m.subtitle}
                onPress={() =>
                  !isLocked && navigate(`/flight-school?moduleId=${m.id}`)
                }
              />
            </div>
          );
        })}

        <PathConnector />
        <ExploreOtherCoursesCard completed={allCompleted} />
      </div>

      {/* FS graduation popup — overlay tap does NOT dismiss (iOS onDismiss is
          a no-op for Flight School, HomeScreen.tsx:408-413). */}
      <CourseCompletedPopup
        visible={showGraduationPopup}
        courseName="Flight School"
        isFlightSchool
        onPickNextCourse={() => {
          setShowGraduationPopup(false);
          void setCurrentCourseID(PROD_TIKTOK_COURSE_ID);
        }}
        onDismiss={() => {}}
      />
    </div>
  );
};

export default FlightSchoolHomePath;
